import { useState, useEffect, useRef } from "react";
import MainView from "views/MainView";
import {
  SelectionTab,
  Tab,
  Description,
  Chart,
  IChartList,
  List,
} from "./components";
import { Spinner, Empty, LoadMore } from "components";
import { FormattedMessage } from "react-intl";
import {
  meterHistory,
  meterStatisticsChart,
  meterUser,
  _H5Event,
  handleFromAppParams,
} from "request";
import { IDataRow, IUser, IPagination } from "../types";
import { useIntlContext, SupportedLocales } from "views/IntlContext";

type IAppParams = {
  token?: string;
  lang?: SupportedLocales;
  deviceId: string;
};

export const StatisticsPage = () => {
  const { switchLanguage } = useIntlContext();

  const [activeTab, setActiveTab] = useState<Tab>(Tab.WEEK);
  const [chartList, setChartList] = useState<IChartList[]>([]);
  const [dataList, setDataList] = useState<IDataRow[]>([]);
  const [users, setUsers] = useState<IUser[]>([]);
  const [currentUser, setCurrentUser] = useState<IUser | undefined>(undefined);
  const [dateStr, setDateStr] = useState<string>("");
  const appParams = useRef<IAppParams | undefined>(undefined);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [hasMore, setHasMore] = useState(false);

  const pagination = useRef<IPagination>({
    pageSize: 10,
    currentPage: 1,
  });

  const onGetUserList = () => {
      const { token, lang, deviceId } = appParams.current as IAppParams;
    meterUser({
      token: token,
      lang: lang,
      params: {
        deviceId: deviceId as string,
      },
    })
      .then((response: any) => {
        if (response.code === "200") {
          setCurrentUser(response.result[0] as IUser);
          setUsers(response.result);
          // getData();
        }
      })
      .catch((err: any) => {
        console.log(err);
      });
  };

  const onGetHistoryList = async () => {
    const { currentPage, pageSize } = pagination.current;
    const { token, lang, deviceId } = appParams.current as IAppParams;

    if (currentPage === 1) {
      setIsLoading(true);
    }

    const params = {
      token: token,
      lang: lang,
      params: {
        deviceId: deviceId as string,
        size: pageSize,
        current: currentPage,
        userId: currentUser?.id as string,
      },
    };

    try {
      const result = await meterHistory(params);
      setIsLoading(false);
      return result;
    } catch (error) {
      setIsLoading(false);
      console.error(
        "An error occurred while fetching the history list:",
        error
      );
      throw error;
    }
    // .then((response: any) => {
    //   if (response.code === "200") {
    //     const list = response?.result?.records as IDataRow[];

    //     setDataList(list);
    //   }
    //   setIsLoading(false);
    // })
    // .catch((err: any) => {
    //   setIsLoading(false);
    //   console.log(err);
    // });
  };

  const onGetChartList = () => {

    const { token, lang, deviceId } = appParams.current as IAppParams;

    meterStatisticsChart({
      token: token,
      lang: lang,
      params: {
        deviceId: deviceId as string,
        size: 10,
        current: 1,
        userId: currentUser?.id as string,
        type: activeTab,
      },
    })
      .then((response: any) => {
        if (response.code === "200") {
          const list = response.result;
          setChartList(list);
        }
      })
      .catch((err: any) => {
        console.log(err);
      });
  };

  const handleResponse = (response: any) => {
    const { code, result } = response;
    if (code === "200") {
      const { records, total, size, current, pages } = result;
      const list = records as IDataRow[];
      if (current === 1) {
        pagination.current = {
          pageSize: size,
          currentPage: current,
          totalPage: pages,
          totalSize: total,
        };
        if (pages > 1) {
          setHasMore(true);
        }
        setDataList(list);
      } else {
        setDataList((prev) => [...prev, ...list]);
      }

      if (current === pages) {
        setHasMore(false);
      }
    }
    setIsLoading(false);
  };

  const onLoadMore = async () => {
    const currentPage = pagination.current.currentPage + 1;
    pagination.current.currentPage = currentPage;
    const response: any = await onGetHistoryList();
    handleResponse(response);
  };

  const onChange = (dataIndex: number) => {
    const selection = chartList[dataIndex];
    setDateStr(selection.date);
  };

  const onToDetailPage = (row: IDataRow) => {
    const datas = {
      event: "ToMeterDetailsPage",
      pageName: "details",
      id: row.id,
      dateStr: row.dateStr,
      deviceId: row.deviceId,
      callback: () => {},
    };
    _H5Event(datas);
  };

  useEffect(() => {
    const datas = {
      event: "GetStatisticsData",
      pageName: "statistics",
      callback: (responseStr: any) => {
        const jsonData = handleFromAppParams(responseStr);
        if (jsonData.lang) {
          switchLanguage?.(jsonData.lang as SupportedLocales);
        }
        appParams.current = jsonData;
        onGetUserList();
      },
    };
    _H5Event(datas);
  }, []);

  useEffect(() => {
    if (currentUser) {
      setDateStr("")
      pagination.current.currentPage = 1
      // setIsLoading(true)
      onGetChartList();
      const fetchData = async () => {
        try {
          const response = await onGetHistoryList();
          handleResponse(response);
        } catch (error) {
          console.error("Failed to fetch the history list:", error);
        }
      };
      fetchData();
    }
  }, [currentUser, activeTab]);

  return (
    <MainView backgroundClassName="bg-page dark:bg-black1b1f1e">
      <div className="bg-[#393c42]">
        <SelectionTab
          activeTab={activeTab}
          onChange={(tab) => setActiveTab(tab)}
        />
        <Description
          users={users}
          currentUser={currentUser}
          dateStr={dateStr}
          onChange={(user) => setCurrentUser(user)}
        />
        <Chart chartList={chartList} onChange={onChange} />
      </div>
      {(isLoading || !dataList || dataList.length === 0) && (
        <div className="w-full h-[40vh] flex items-center justify-center">
          {isLoading ? (
            <Spinner miniSmall className={{ "border-primary": true }} />
          ) : <Empty
              emptyText={<FormattedMessage id="meter.history.emptyText" />}
            />}
        </div>
      )}
      {!isLoading && dataList.length > 0 && (
        <>
          <List dataList={dataList} onToDetailPage={onToDetailPage} />
          <LoadMore loadMore={onLoadMore} hasMore={hasMore} />
        </>
      )}
    </MainView>
  );
};
